/**
 * DatePicker Component
 *
 * A cross-platform date picker that works on iOS, Android, and Web.
 * On iOS/Android: Shows native date picker when tapped.
 * On Web: Shows HTML5 date input.
 */

import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform, TextInput } from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';
import { Ionicons } from '@expo/vector-icons';

// Lazy load DateTimePicker for native platforms to avoid web build issues
let DateTimePicker: any = null;
if (Platform.OS !== 'web') {
  try {
    DateTimePicker = require('@react-native-community/datetimepicker');
  } catch (e) {
    console.warn('DateTimePicker not available');
  }
}

interface DatePickerProps {
  value: string | null;
  onChange: (date: string | null) => void;
  placeholder?: string;
  minimumDate?: Date;
  maximumDate?: Date;
  testID?: string;
}

export function DatePicker({
  value,
  onChange,
  placeholder = 'YYYY-MM-DD',
  minimumDate,
  maximumDate,
  testID,
}: DatePickerProps) {
  const { theme } = useTheme();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const [showPicker, setShowPicker] = useState(false);

  const handlePress = () => {
    if (Platform.OS === 'web') {
      // Web uses the native input element via TextInput with secureTextEnter
      // We'll let the TextInput handle it
      return;
    }
    setShowPicker(true);
  };

  const handleDateChange = (event: any, selectedDate?: Date) => {
    setShowPicker(Platform.OS === 'ios');

    if (Platform.OS === 'android') {
      setShowPicker(false);
    }

    if (selectedDate) {
      // Format as YYYY-MM-DD
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      onChange(`${year}-${month}-${day}`);
    } else if (Platform.OS === 'ios' && event.type === 'dismissed') {
      // On iOS, when dismissed without selecting, keep existing value
      // Don't clear the date
    }
  };

  const handleClear = () => {
    onChange(null);
  };

  const displayValue = value || '';

  if (Platform.OS === 'web') {
    return (
      <View
        testID={testID}
        style={[styles.container, { backgroundColor: theme.colors.backgroundElevated }]}
      >
        <Ionicons name="calendar-outline" size={20} color={theme.colors.textSecondary} />
        <View style={{ flex: 1 }}>
          <input
            type="date"
            value={value || ''}
            onChange={(e) => onChange(e.target.value || null)}
            style={{
              width: '100%',
              border: 'none',
              background: 'transparent',
              fontSize: '16px',
              color: theme.colors.text,
              fontFamily: 'inherit',
            }}
          />
        </View>
        {value && (
          <TouchableOpacity
            onPress={handleClear}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Ionicons name="close-circle" size={18} color={theme.colors.textSecondary} />
          </TouchableOpacity>
        )}
      </View>
    );
  }

  if (!DateTimePicker) {
    return (
      <View
        testID={testID}
        style={[styles.container, { backgroundColor: theme.colors.backgroundElevated }]}
      >
        <Ionicons name="calendar-outline" size={20} color={theme.colors.textSecondary} />
        <TextInput
          style={[styles.input, { color: theme.colors.text }]}
          placeholder={placeholder}
          placeholderTextColor={theme.colors.textSecondary}
          value={displayValue}
          onChangeText={(text) => {
            const validChars = /^[0-9-]*$/;
            if (text === '' || (validChars.test(text) && text.length <= 10)) {
              onChange(text || null);
            }
          }}
        />
        {value && (
          <TouchableOpacity
            onPress={handleClear}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Ionicons name="close-circle" size={18} color={theme.colors.textSecondary} />
          </TouchableOpacity>
        )}
      </View>
    );
  }
  const dateValue = value ? new Date(value) : new Date();
  return (
    <View>
      <TouchableOpacity
        testID={testID}
        style={[styles.container, { backgroundColor: theme.colors.backgroundElevated }]}
        onPress={handlePress}
      >
        <Ionicons name="calendar-outline" size={20} color={theme.colors.textSecondary} />
        <Text
          style={[styles.input, { color: value ? theme.colors.text : theme.colors.textSecondary }]}
        >
          {displayValue || placeholder}
        </Text>
        {value && (
          <TouchableOpacity
            onPress={handleClear}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            onPressIn={(e) => e.stopPropagation()}
          >
            <Ionicons name="close-circle" size={18} color={theme.colors.textSecondary} />
          </TouchableOpacity>
        )}
      </TouchableOpacity>
      {showPicker && (
        <DateTimePicker
          value={dateValue}
          mode="date"
          display={Platform.OS === 'ios' ? 'compact' : 'default'}
          onChange={handleDateChange}
          minimumDate={minimumDate}
          maximumDate={maximumDate}
          style={{ width: 120 }}
        />
      )}
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      flexDirection: 'row',
      alignItems: 'center',
      borderRadius: theme.id === 'bauhaus' ? 0 : theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderColor: theme.colors.border,
      paddingHorizontal: 12,
      paddingVertical: 10,
      gap: 10,
      minHeight: 44,
    },
    input: {
      flex: 1,
      fontSize: 16,
    },
  });
}
